import { useState, useCallback } from "react";
import { useOnboardingData } from "features/User/hooks/useOnboardingData";
import useSubscription from "features/User/hooks/useSubscription";
import { emailHasStudentDomain } from "helpers/email";
import { getSubscriptionTier } from "helpers/subscription";
import { useAppDispatch } from "hooks/useStore";
import { replaceTopModal } from "store/slices/modalStack";
import { Role } from "features/auth/types/onboarding";
import {
  StudentEmailError,
  StudentEmailErrorType,
} from "features/auth/types/studentEmailErrors";
import {
  STUDENT_DISCOUNT_MODAL_ID,
  StudentDiscountModalProps,
} from "features/Subscription/components/StudentDiscountModal";
import { isStudentRole, STUDENT_ROLES } from "features/auth/utils/roleUtils";
import { useRoleAndStudentEmail } from "features/auth/hooks/useRoleAndStudentEmail";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";

interface UseClaimStudentDiscountReturn {
  // State
  selectedRole: Role | null;
  studentEmail: string;
  studentEmailError: StudentEmailErrorType | null;
  showVerification: boolean;
  isProcessing: boolean;
  unverifiedEmail: string | null;

  // Handlers
  handleRoleSelect: (role: Role) => void;
  handleEmailChange: (email: string) => void;
  handleSubmit: () => Promise<void>;
  handleVerificationComplete: () => Promise<void>;
  handleSkipVerification: () => void;
  handleChangeEmail: () => void;
  resetState: () => void;

  // Derived state
  existingStudentEmail: string | null;
  needsStudentEmail: boolean;
  isSubmitDisabled: boolean;
}

/**
 * Hook for managing the student discount claim flow
 *
 * @example
 * ```tsx
 * const {
 *   selectedRole,
 *   handleRoleSelect,
 *   // ...
 * } = useClaimStudentDiscount();
 * ```
 *
 * @param actionSource - The source where the modal was opened from
 * @returns {UseClaimStudentDiscountReturn} Hook state and handlers
 */
export function useClaimStudentDiscount(
  actionSource?: MixpanelActionSource
): UseClaimStudentDiscountReturn {
  const { updateOnboardingData, role: currentRole } = useOnboardingData();
  const subscription = useSubscription();
  const dispatch = useAppDispatch();

  // Determine if user is premium to select correct modal type
  const subscriptionTier = getSubscriptionTier(subscription?.subscription);
  const isPremiumUser = subscriptionTier === "premium";

  // Use shared hook for role and student email management
  const {
    selectedRole,
    studentEmail,
    studentEmailError,
    showVerification,
    unverifiedEmail,
    existingStudentEmail,
    needsStudentEmail,
    handleRoleSelect,
    handleStudentEmailChange: handleEmailChange,
    setShowVerification,
    setUnverifiedEmail,
    setStudentEmailError,
    resetEmailInput,
    resetVerification,
  } = useRoleAndStudentEmail({
    initialRole: currentRole,
  });

  // Additional state for processing
  const [isProcessing, setIsProcessing] = useState(false);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!selectedRole || isProcessing) return;

    setIsProcessing(true);

    try {
      // Non-student role: update metadata and close
      if (!isStudentRole(selectedRole)) {
        await updateOnboardingData({ role: selectedRole }, "student-discount");
        setIsProcessing(false);
        // Modal will be closed by the component
        return;
      }

      // Student role with existing email: proceed to discount modal
      if (!needsStudentEmail) {
        await updateOnboardingData({ role: selectedRole }, "student-discount");
        const modalType = isPremiumUser ? "deep" : "premium";
        const modalProps: StudentDiscountModalProps = {
          type: modalType,
          role: selectedRole as (typeof STUDENT_ROLES)[number],
          actionSource: actionSource || MixpanelActionSource.Onboarding,
        };
        dispatch(
          replaceTopModal({
            id: STUDENT_DISCOUNT_MODAL_ID,
            blocksScroll: true,
            props: modalProps,
          })
        );
        return;
      }

      // Student role needs email: validate and go to verification
      if (!studentEmail) {
        setStudentEmailError(StudentEmailError.INVALID_EMAIL);
        setIsProcessing(false);
        return;
      }

      if (!emailHasStudentDomain(studentEmail)) {
        setStudentEmailError(StudentEmailError.NOT_STUDENT_EMAIL);
        setIsProcessing(false);
        return;
      }

      // Email is valid, proceed to verification
      setUnverifiedEmail(studentEmail);
      setShowVerification(true);
      setIsProcessing(false);
    } catch (error) {
      console.error("Error submitting:", error);
      setIsProcessing(false);
    }
  }, [
    selectedRole,
    isProcessing,
    needsStudentEmail,
    studentEmail,
    updateOnboardingData,
    dispatch,
    isPremiumUser,
    setUnverifiedEmail,
    setShowVerification,
    setStudentEmailError,
  ]);

  // Reset all state - useful when modal closes or transitions
  const resetState = useCallback(() => {
    setIsProcessing(false);
    resetEmailInput(); // Resets: showVerification, studentEmail, studentEmailError, unverifiedEmail
  }, [resetEmailInput]);

  // Handle verification completion
  const handleVerificationComplete = useCallback(async () => {
    setIsProcessing(true);

    try {
      // Save role and verified email to metadata
      await updateOnboardingData(
        {
          role: selectedRole!,
          verified_student_email: unverifiedEmail!,
        },
        "student-discount"
      );

      // Open discount modal with type based on subscription
      const modalType = isPremiumUser ? "deep" : "premium";
      const modalProps: StudentDiscountModalProps = {
        type: modalType,
        role: selectedRole as (typeof STUDENT_ROLES)[number],
        actionSource: actionSource || MixpanelActionSource.Onboarding,
      };
      dispatch(
        replaceTopModal({
          id: STUDENT_DISCOUNT_MODAL_ID,
          blocksScroll: true,
          props: modalProps,
        })
      );
      // Reset state after transitioning to the student discount modal
      resetState();
    } catch (error) {
      console.error("Error completing verification:", error);
      setIsProcessing(false);
    }
  }, [
    selectedRole,
    unverifiedEmail,
    updateOnboardingData,
    dispatch,
    isPremiumUser,
    resetState,
    actionSource,
  ]);

  // Handle skip verification
  const handleSkipVerification = useCallback(() => {
    // Go back to email input
    resetEmailInput();
  }, [resetEmailInput]);

  // Handle change email from verification
  const handleChangeEmail = useCallback(() => {
    // Go back to email input from verification
    resetVerification();
  }, [resetVerification]);

  // Determine if submit is disabled
  const isSubmitDisabled =
    !selectedRole || isProcessing || (needsStudentEmail && !studentEmail);

  return {
    // State
    selectedRole,
    studentEmail,
    studentEmailError,
    showVerification,
    isProcessing,
    unverifiedEmail,

    // Handlers
    handleRoleSelect,
    handleEmailChange,
    handleSubmit,
    handleVerificationComplete,
    handleSkipVerification,
    handleChangeEmail,
    resetState,

    // Derived state
    existingStudentEmail,
    needsStudentEmail,
    isSubmitDisabled,
  };
}
